/**
 * Client-side mirror of the backend dim-value guards
 * (`repositories/cube/management.py`): values collide when they match
 * ignoring case and whitespace, except a rename may re-spell the value it
 * renames as long as the target spelling itself is unoccupied.
 *
 * The backend probes the *destination slice* (the mapped values combined,
 * within the untouched context selection), which the per-dimension option
 * lists here cannot decide — see {@link targetsWholeDimension} for when
 * these checks are usable live.
 */

/**
 * Whether an operation mapping `key` covers the whole dimension — no other
 * dimension is selected — so that per-dimension existence alone proves a
 * collision. Any narrower context (adding store X in Denmark from a German
 * template) collides only when the stored *combination* exists, which these
 * option lists cannot tell: skip the live check and let the backend guard
 * decide on submit (its reason surfaces in the error toast).
 */
export function targetsWholeDimension(
  selections: Record<string, string[]>,
  key: string
): boolean {
  return Object.entries(selections).every(
    ([dim, values]) => dim === key || values.length === 0
  );
}

/** The comparison form of a dim value (mirrors backend `normalized_dim_value`). */
export function normalizeDimValue(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase();
}

/** Stored spellings in `existing` matching `value`, ignoring case/whitespace. */
export function storedDimValues(existing: string[], value: string): string[] {
  const normalized = normalizeDimValue(value);
  return existing.filter((e) => normalizeDimValue(e) === normalized);
}

/** Error for introducing `value` as a brand-new dim value (Add / Clone). */
export function newDimValueError(
  existing: string[],
  value: string
): string | undefined {
  return storedDimValues(existing, value).length > 0
    ? "This value already exists."
    : undefined;
}

/** Error for renaming `selected` to `value` (mirrors the backend rename guard). */
export function renameDimValueError(
  existing: string[],
  selected: string,
  value: string
): string | undefined {
  const stored = storedDimValues(existing, value);
  const respellingOnly =
    normalizeDimValue(selected) === normalizeDimValue(value);
  if (
    stored.length === 0 ||
    (respellingOnly && !stored.includes(value.trim()))
  ) {
    return undefined;
  }
  return "This value already exists. Use Merge Dimension to combine values.";
}
